import { CloseIcon, TimeIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  useOutsideClick,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const RECENT_SEARCHES_KEY = 'recent_searches';
const MAX_RECENT_SEARCHES = 10;

const HomeSearchSection = () => {
  const [search, setSearch] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [isRecentOpen, setIsRecentOpen] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const searchContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setRecentSearches(
            parsed.filter(
              (item): item is string => typeof item === 'string'
            )
          );
        }
      }
    } catch (err) {
      console.error(
        'Failed to load recent searches from localStorage',
        err
      );
    }
  }, []);

  useOutsideClick({
    ref: searchContainerRef,
    handler: () => setIsRecentOpen(false),
  });

  const executeSearch = (rawQuery: string) => {
    const trimmedQuery = rawQuery.trim();

    if (!trimmedQuery) {
      toast({
        title: 'Search Validation',
        description:
          'Please fill in the input field to perform the search.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      setError('Please fill in the input field to perform the search.');
      return;
    }

    setError('');
    navigate(`/search/${encodeURIComponent(trimmedQuery)}`);

    setRecentSearches((prev) => {
      const withoutDuplicate = prev.filter(
        (item) => item.toLowerCase() !== trimmedQuery.toLowerCase()
      );
      const updated = [trimmedQuery, ...withoutDuplicate].slice(
        0,
        MAX_RECENT_SEARCHES
      );
      try {
        localStorage.setItem(
          RECENT_SEARCHES_KEY,
          JSON.stringify(updated)
        );
      } catch (err) {
        console.error(
          'Failed to save recent searches to localStorage',
          err
        );
      }
      return updated;
    });

    setSearch('');
    setIsRecentOpen(false);
  };

  const navigateToSearch = () => {
    executeSearch(search);
  };

  const handleKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Enter') {
      navigateToSearch();
    } else if (event.key === 'Escape') {
      setIsRecentOpen(false);
    }
  };

  const clearHistory = () => {
    setRecentSearches([]);
    try {
      localStorage.removeItem(RECENT_SEARCHES_KEY);
    } catch (err) {
      console.error(
        'Failed to clear recent searches from localStorage',
        err
      );
    }
    setIsRecentOpen(false);
  };

  const removeRecentItem = (term: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setRecentSearches((prev) => {
      const updated = prev.filter((item) => item !== term);
      try {
        if (updated.length === 0) {
          localStorage.removeItem(RECENT_SEARCHES_KEY);
        } else {
          localStorage.setItem(
            RECENT_SEARCHES_KEY,
            JSON.stringify(updated)
          );
        }
      } catch (err) {
        console.error(
          'Failed to update recent searches in localStorage',
          err
        );
      }
      return updated;
    });
  };

  return (
    <Flex
      w="100%"
      maxW="100%"
      px={{ base: 4, md: 6 }}
      py={6}
      gap={4}
      flexDirection="column"
      alignItems="stretch"
      borderRadius="lg"
      backgroundColor="background.blue50"
      borderWidth="1px"
      borderColor="border.subtle"
      boxShadow="sm"
    >
      <Text
        fontSize={{ base: 'lg', md: 'xl' }}
        fontWeight="700"
        lineHeight="1.25"
        color="text.primary"
        textAlign={{ base: 'left', md: 'center' }}
      >
        Search across CHS practices, providers, or phone numbers
      </Text>
      <Flex
        gap={3}
        position="relative"
        ref={searchContainerRef}
        flexWrap={{ base: 'wrap', md: 'nowrap' }}
        justify={{ base: 'stretch', md: 'center' }}
        align="flex-start"
      >
        <Box position="relative" flex="1" minW={{ base: '100%', md: '20rem' }} maxW={{ md: '32rem' }}>
          <Input
            placeholder="Search practices, providers, or phone numbers"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDownCapture={handleKeyPress}
            onFocus={() => {
              if (recentSearches.length > 0) {
                setIsRecentOpen(true);
              }
            }}
            variant="filled"
            w="100%"
            h="2.5rem"
            padding="0rem 0.75rem"
            borderRadius={'0.375rem'}
            border="1px solid"
            borderColor={error ? 'red.500' : 'border.gray'}
            background="white"
            _hover={{
              border: error
                ? '1px solid red.500'
                : '1px solid border.gray',
            }}
          />

          {isRecentOpen && recentSearches.length > 0 && (
            <Box
              position="absolute"
              top="100%"
              left={0}
              right={0}
              mt={1}
              bg="white"
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="md"
              boxShadow="md"
              zIndex={10}
              maxH="14rem"
              overflowY="auto"
            >
              <Flex
                align="center"
                justify="space-between"
                px={3}
                py={2}
                borderBottomWidth="1px"
                borderColor="gray.100"
              >
                <Text
                  fontSize="xs"
                  fontWeight="600"
                  color="gray.500"
                >
                  Recent searches
                </Text>
                <Button
                  variant="link"
                  size="xs"
                  color="gray.500"
                  onClick={clearHistory}
                >
                  Clear history
                </Button>
              </Flex>

              {recentSearches.map((term) => (
                <Flex
                  key={term}
                  align="center"
                  justify="space-between"
                  px={3}
                  py={2}
                  cursor="pointer"
                  _hover={{ bg: 'gray.100' }}
                  onClick={() => executeSearch(term)}
                >
                  <Flex align="center" gap={2}>
                    <TimeIcon boxSize={3} color="gray.400" />
                    <Text
                      fontSize="sm"
                      color="gray.700"
                      noOfLines={1}
                    >
                      {term}
                    </Text>
                  </Flex>
                  <Button
                    variant="ghost"
                    size="xs"
                    aria-label={`Remove ${term} from history`}
                    onClick={(e) => removeRecentItem(term, e)}
                    _hover={{ bg: 'gray.200' }}
                  >
                    <CloseIcon boxSize={2.5} />
                  </Button>
                </Flex>
              ))}
            </Box>
          )}
        </Box>
        <Button
          variant="solid"
          flexShrink={0}
          h="2.5rem"
          px={5}
          borderRadius="md"
          bg="background.brandDark"
          color="white"
          fontSize="md"
          fontWeight={600}
          lineHeight={1.5}
          _hover={{
            bg: 'background.brandLight',
          }}
          _active={{
            bg: 'background.brandDark',
          }}
          onClick={navigateToSearch}
        >
          Search
        </Button>
      </Flex>
    </Flex>
  );
};

export default HomeSearchSection;
